import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Card, Avatar, Typography } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import COLORS from "common/Colors";
import { get, startCase } from "lodash";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

const { Text } = Typography;

/* ===========================================================
    COLOR PALETTE (per manager column)
=========================================================== */

const PALETTE = [
  { bg: "#EFF6FF", border: "#BFDBFE", line: "#2563EB" }, // blue
  { bg: "#F0FDF4", border: "#BBF7D0", line: "#16A34A" }, // green
  { bg: "#FFFBEB", border: "#FDE68A", line: "#D97706" }, // amber
  { bg: "#F5F3FF", border: "#DDD6FE", line: "#7C3AED" }, // purple
  { bg: "#FDF2F8", border: "#FBCFE8", line: "#DB2777" }, // pink
];

const LINE_COLOR = "#2563EB";

/* ===========================================================
    LAYOUT PRIMITIVES
=========================================================== */

const Wrapper = styled.div`
  width: 100%;
  background: #f6f8fb;
  padding: 40px 24px;
  overflow-x: auto;
`;

const ChartContainer = styled.div`
  min-width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CEOWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
`;

const CEOSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 48px;
  position: relative;
`;

const CEOLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 16px);
  height: 2px;
  background: ${LINE_COLOR};
  display: ${(props) => (props.$visible ? "block" : "none")};
`;

const CEOColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;

  /* vertical stem dropping from the card down to the shared line */
  &::after {
    content: "";
    display: ${(props) => (props.$showStem ? "block" : "none")};
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 16px;
    background: ${LINE_COLOR};
  }

  /* horizontal segment of the shared line — same bridging technique as ManagerColumn */
  &::before {
    content: "";
    display: ${(props) => (props.$isOnly ? "none" : "block")};
    position: absolute;
    top: calc(100% + 16px);
    height: 2px;
    background: ${LINE_COLOR};
    left: ${(props) => (props.$isFirst ? "50%" : "-24px")};
    right: ${(props) => (props.$isLast ? "50%" : "-24px")};
  }
`;

const CEOStem = styled.div`
  width: 2px;
  height: 36px;
  background: ${LINE_COLOR}; /* no margin-left */
`;

const ManagersRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;
  position: relative;
`;

const ManagerColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 260px;
  max-width: 300px;
  padding-top: 36px;

  /* vertical stem dropping to this manager */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 2px;
    height: 36px;
    background: ${LINE_COLOR};
  }

  /* horizontal segment of the shared branch line -
     extends past the column edge by half the row gap so it
     bridges seamlessly into the next/previous column's segment */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    height: 2px;
    background: ${LINE_COLOR};
    left: ${(props) => (props.$isFirst ? "50%" : "-24px")};
    right: ${(props) => (props.$isLast ? "50%" : "-24px")};
    display: ${(props) => (props.$isOnly ? "none" : "block")};
  }
`;

const BranchDot = styled.div`
  position: absolute;
  top: -5px;
  left: calc(50% - 5px);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${LINE_COLOR};
`;

const ManagerStem = styled.div`
  width: 2px;
  height: 28px;
  background: ${LINE_COLOR};
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  margin-top: 4px;
  border-radius: 10px;
  border: 1.5px dashed ${(props) => props.$border || "#BFDBFE"};
  background: #ffffff;
  color: ${(props) => props.$color || "#2563EB"};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => props.$bg || "#EFF6FF"};
  }

  .anticon {
    font-size: 11px;
  }
`;

const EmployeeContainer = styled.div`
  width: 100%;
  margin-top: -1px;
  padding: 20px 16px;
  border-radius: 16px;
  background: ${(props) => props.$bg};
  border: 2px dashed ${(props) => props.$border};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

/* ===========================================================
    USER CARD
=========================================================== */

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const LevelBadge = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  width: 32px;
  height: 32px;
  background: #2563eb;
  color: #ffffff;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
`;

const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 14px;
  border: 1px solid ${(props) => props.$border || "#eef1f6"};
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);

  .ant-card-body {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1; /* NEW - Details absorbs the extra/limited space instead */
  min-width: 0; /* keep this so long text can still wrap/ellipsis */
`;

const Name = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Designation = styled(Text)`
  font-size: 13px;
  color: #6b7280;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #2563eb;

  .anticon {
    color: #2563eb;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const StyledAvatar = styled(Avatar)`
  font-size: 2rem;
  height: 60px;
  width: 60px;
  flex-shrink: 0; /* NEW - avatar can never be compressed by flex */
  flex-grow: 0;
  background-color: ${COLORS.PRIMARY};
  vertical-align: middle;
  .ant-avatar-string {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const sortByName = (users) =>
  [...users].sort((a, b) =>
    (a?.fullname || "").localeCompare(b?.fullname || "", undefined, {
      sensitivity: "base",
    })
  );

const UserCard = ({ user, level, accent }) => {
  return (
    <CardWrapper>
      <LevelBadge>{level}</LevelBadge>

      <StyledCard $border={accent}>
        <Header>
          {user?.profileurl === null ||
          user?.profileurl === "" ||
          Array.isArray(user?.profileurl) ? (
            <StyledAvatar size="default">
              {startCase(user?.fullname).charAt(0)}
            </StyledAvatar>
          ) : (
            <StyledAvatar src={user?.profileurl} size="default" />
          )}
          {/* <StyledAvatar
            size={56}
            src={user?.profileurl}
            icon={<UserOutlined />}
          /> */}

          <Details>
            <Name>{user?.fullname || "Unknown"}</Name>
            <Designation>{user?.jobtitle || "-"}</Designation>
          </Details>
        </Header>

        <EmailRow>
          <MailOutlined />
          <span>{user?.email || user?.userid}</span>
        </EmailRow>
      </StyledCard>
    </CardWrapper>
  );
};

/* ===========================================================
    MAIN COMPONENT
=========================================================== */

export default function OrganizationalChart() {
  /* ==========================================
      FIND CEO
  ========================================== */
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const HigherOfficialsList = userInfo?.higherOfficialsList;

  /* ==========================================
      L3 SHOW MORE / SHOW LESS
      Each manager column starts by showing only the first
      PAGE_SIZE employees. Clicking "+" reveals PAGE_SIZE more;
      clicking "-" collapses back down to PAGE_SIZE.
  ========================================== */
  const PAGE_SIZE = 5;
  const [expandedCounts, setExpandedCounts] = useState({});

  const getVisibleCount = (managerId, total) =>
    Math.min(expandedCounts[managerId] || PAGE_SIZE, total);

  const handleExpand = (managerId, total) => {
    setExpandedCounts((prev) => ({
      ...prev,
      [managerId]: Math.min((prev[managerId] || PAGE_SIZE) + PAGE_SIZE, total),
    }));
  };

  const handleCollapse = (managerId) => {
    setExpandedCounts((prev) => ({
      ...prev,
      [managerId]: PAGE_SIZE,
    }));
  };
  const activeUserList = useMemo(
    () => userList?.filter((user) => user?.statusid === "1") || [],
    [userList]
  );

  const ceos = useMemo(() => {
    const ceoOfficials = (HigherOfficialsList || []).filter(
      (item) => item.role?.toUpperCase() === "CEO"
    );

    if (!ceoOfficials.length) return [];

    return sortByName(
      activeUserList.filter((user) => {
        const userId = (user.userid || "").toLowerCase().trim();
        const email = (user.email || "").toLowerCase().trim();

        return ceoOfficials.some((ceoOfficial) => {
          const ceoEmail = (ceoOfficial.email || "").toLowerCase().trim();
          return userId === ceoEmail || email === ceoEmail;
        });
      })
    );
  }, [HigherOfficialsList, activeUserList]);

  // Track CEOs by BOTH userid and email so any manager lookup that
  // resolves to a CEO (regardless of which field it matched on) can
  // be reliably excluded from the L2 managers list below.
  const ceoIdentifierSet = useMemo(() => {
    const set = new Set();
    ceos.forEach((ceoUser) => {
      const userId = (ceoUser.userid || "").toLowerCase().trim();
      const email = (ceoUser.email || "").toLowerCase().trim();
      if (userId) set.add(userId);
      if (email) set.add(email);
    });
    return set;
  }, [ceos]);

  const ceoUserIds = useMemo(
    () => new Set(ceos.map((user) => (user.userid || "").toLowerCase().trim())),
    [ceos]
  );

  const nonCeos = useMemo(
    () =>
      activeUserList.filter(
        (user) => !ceoUserIds.has((user.userid || "").toLowerCase().trim())
      ),
    [activeUserList, ceoUserIds]
  );

  /* ==========================================
      REPORTING MANAGERS (deduped, in order of first appearance)
      Guard: a CEO must never be classified as an L2 manager via
      the relationship-detection loop, even if they slip through
      the nonCeos filter due to a userid/email mismatch.

      Separately, if a CEO has employees reporting directly to
      them (no manager in between), the CEO is re-added here as
      their own L2 entry — a duplicate card — so those employees
      still render, grouped under the CEO at L3 like any other
      manager's team.
  ========================================== */

  const managers = useMemo(() => {
    const managerMap = {};

    nonCeos.forEach((employee) => {
      if (!employee.reportingmanager) return;

      const employeeId = (employee.userid || "").toLowerCase().trim();
      const employeeEmail = (employee.email || "").toLowerCase().trim();
      const reporting = employee.reportingmanager.toLowerCase().trim();

      // Bad data guard: an employee set as their own reporting manager
      // must never turn them into their own manager/report.
      if (
        reporting === employeeId ||
        (employeeEmail && reporting === employeeEmail)
      ) {
        return;
      }

      const manager = nonCeos.find((user) => {
        const id = (user.userid || "").toLowerCase().trim();
        const email = (user.email || "").toLowerCase().trim();

        return id === reporting || email === reporting;
      });

      if (!manager) return;

      const managerId = (manager.userid || "").toLowerCase().trim();
      const managerEmail = (manager.email || "").toLowerCase().trim();

      // Explicit safety check: skip if this "manager" is actually a CEO
      if (
        ceoIdentifierSet.has(managerId) ||
        ceoIdentifierSet.has(managerEmail)
      ) {
        return;
      }

      if (!managerMap[managerId]) {
        managerMap[managerId] = manager;
      }
    });

    // Re-add each CEO as their own L2 entry if anyone reports to
    // them directly, so that group of employees still gets a home
    // in the chart (as L3 under this duplicate CEO card).
    ceos.forEach((ceoUser) => {
      const ceoId = (ceoUser.userid || "").toLowerCase().trim();
      const ceoEmail = (ceoUser.email || "").toLowerCase().trim();

      const hasDirectReports = nonCeos.some((employee) => {
        const reporting = (employee.reportingmanager || "")
          .toLowerCase()
          .trim();
        return reporting && (reporting === ceoId || reporting === ceoEmail);
      });

      if (hasDirectReports) {
        managerMap[ceoId] = ceoUser;
      }
    });

    return sortByName(Object.values(managerMap));
  }, [nonCeos, ceos, ceoIdentifierSet]);

  /* ==========================================
      GROUP EMPLOYEES UNDER EACH MANAGER
  ========================================== */

  const managerWiseEmployees = useMemo(() => {
    const obj = {};

    managers.forEach((manager) => {
      const managerId = (manager.userid || "").toLowerCase().trim();
      const managerEmail = (manager.email || "").toLowerCase().trim();

      obj[manager.userid] = sortByName(
        activeUserList.filter((employee) => {
          const employeeId = (employee.userid || "").toLowerCase().trim();
          const employeeEmail = (employee.email || "").toLowerCase().trim();

          // Bad data guard: never list a manager as their own direct report.
          if (
            employeeId === managerId ||
            (employeeEmail && employeeEmail === managerEmail)
          ) {
            return false;
          }

          const reporting = (employee.reportingmanager || "")
            .toLowerCase()
            .trim();
          return (
            reporting &&
            (reporting === managerId || reporting === managerEmail)
          );
        })
      );
    });

    return obj;
  }, [managers, activeUserList]);

  /* ==========================================
      ACTIVE USERS THAT NEVER RESOLVED INTO THE
      TREE ABOVE (no manager match, or nobody
      reports to them) — surfaced separately so
      they don't just silently vanish from the chart.
  ========================================== */

  const unassignedUsers = useMemo(() => {
    const placedIds = new Set();

    ceos.forEach((user) =>
      placedIds.add((user.userid || "").toLowerCase().trim())
    );
    managers.forEach((user) =>
      placedIds.add((user.userid || "").toLowerCase().trim())
    );
    Object.values(managerWiseEmployees).forEach((employeeList) => {
      employeeList.forEach((user) =>
        placedIds.add((user.userid || "").toLowerCase().trim())
      );
    });

    return sortByName(
      activeUserList.filter(
        (user) => !placedIds.has((user.userid || "").toLowerCase().trim())
      )
    );
  }, [activeUserList, ceos, managers, managerWiseEmployees]);

  return (
    <Wrapper>
      <ChartContainer>
        {/* Level 1 - CEO */}
        <CEOWrapper>
          <CEOSection>
            {ceos.map((ceoUser, index) => (
              <CEOColumn
                key={ceoUser.userid}
                $showStem={ceos.length > 1}
                $isFirst={index === 0}
                $isLast={index === ceos.length - 1}
                $isOnly={ceos.length === 1}
              >
                <UserCard user={ceoUser} level="L1" />
              </CEOColumn>
            ))}
          </CEOSection>

          {ceos.length > 1 && <CEOStem />}
        </CEOWrapper>

        {/* Level 2 & 3 - Managers + their employees */}
        <ManagersRow>
          {managers.map((manager, index) => {
            const totalColumns = managers.length + unassignedUsers.length;
            const isFirst = index === 0;
            const isLast =
              index === managers.length - 1 && unassignedUsers.length === 0;
            const isOnly = totalColumns === 1;
            const colors = PALETTE[index % PALETTE.length];
            const employees = managerWiseEmployees[manager.userid] || [];

            return (
              <ManagerColumn
                key={manager.userid}
                $isFirst={isFirst}
                $isLast={isLast}
                $isOnly={isOnly}
              >
                <BranchDot />

                <UserCard level="L2" user={manager} accent={colors.border} />

                {employees.length > 0 &&
                  (() => {
                    const visibleCount = getVisibleCount(
                      manager.userid,
                      employees.length
                    );
                    const visibleEmployees = employees.slice(0, visibleCount);
                    const hasMore = visibleCount < employees.length;
                    const isExpanded = visibleCount > PAGE_SIZE;

                    return (
                      <>
                        <ManagerStem />
                        <EmployeeContainer
                          $bg={colors.bg}
                          $border={colors.border}
                        >
                          {visibleEmployees.map((emp) => (
                            <UserCard
                              key={emp.userid}
                              level="L3"
                              user={emp}
                              accent={colors.border}
                            />
                          ))}

                          {hasMore && (
                            <ExpandButton
                              type="button"
                              $border={colors.border}
                              $color={colors.line}
                              $bg={colors.bg}
                              onClick={() =>
                                handleExpand(manager.userid, employees.length)
                              }
                            >
                              <PlusOutlined />
                              Show{" "}
                              {Math.min(
                                PAGE_SIZE,
                                employees.length - visibleCount
                              )}{" "}
                              more
                            </ExpandButton>
                          )}

                          {!hasMore && isExpanded && (
                            <ExpandButton
                              type="button"
                              $border={colors.border}
                              $color={colors.line}
                              $bg={colors.bg}
                              onClick={() => handleCollapse(manager.userid)}
                            >
                              <MinusOutlined />
                              Show less
                            </ExpandButton>
                          )}
                        </EmployeeContainer>
                      </>
                    );
                  })()}
              </ManagerColumn>
            );
          })}

          {/* Active users with no resolvable manager relationship —
              still surfaced here instead of disappearing from the chart. */}
          {unassignedUsers.map((user, uIndex) => {
            const index = managers.length + uIndex;
            const totalColumns = managers.length + unassignedUsers.length;
            const isFirst = index === 0;
            const isLast = index === totalColumns - 1;
            const isOnly = totalColumns === 1;
            const colors = PALETTE[index % PALETTE.length];

            return (
              <ManagerColumn
                key={user.userid}
                $isFirst={isFirst}
                $isLast={isLast}
                $isOnly={isOnly}
              >
                <BranchDot />
                <UserCard level="L2" user={user} accent={colors.border} />
              </ManagerColumn>
            );
          })}
        </ManagersRow>
      </ChartContainer>
    </Wrapper>
  );
}
